//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ColumnModel } from '../../modules/api/api.models'

import { LocalizationHelper, ConvertHelper, ToastHelper, LogHelper, Dictionary, KeyType } from '../../helpers'
import { SequentialItemSaverResult, SequentialItemSaver, SequentialItemSaverMode, SequentialItemSaverStatus, SequentialItemSaverProgress } from '../../helpers/sequential-item-saver'

import { Enums } from '../../models/common'
import { OpPackage, OpDepositoryElement, OpLocation } from '../../models/core'
import { OpValidateInfo, OpValidateInfoMessage } from '../../models/custom'

import { PackageService, PACKAGE_SERVICE_NAME } from '../package'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'


//#region Public Types

export const ORDER_COMPLETE_SERVICE_NAME = 'OrderCompleteOrderService'
export const ORDER_COMPLETE_SERVICE_PROVIDER_NAME = 'OrderCompleteOrderServiceProvider'

//#endregion


export class OrderCompleteService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Services.Order.CompleteOrder]' }

    //#endregion


    //#region Ctor

    constructor(
        private packageService: PackageService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Columns

    public getContentGridColumns(isContentEditable: boolean = false): ColumnModel[] {
        const orderColumn: ColumnModel = new ColumnModel()

        orderColumn.idName = 'package'
        orderColumn.name = 'package'
        orderColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Order)
        orderColumn.type = Enums.DataTypeClass.Text

        const articleColumn: ColumnModel = new ColumnModel()

        articleColumn.idName = 'article'
        articleColumn.name = 'article'
        articleColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Type)
        articleColumn.type = Enums.DataTypeClass.Text

        const serialNumberColumn: ColumnModel = new ColumnModel()

        serialNumberColumn.idName = 'externalSn'
        serialNumberColumn.name = 'externalSn'
        serialNumberColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SerialNbr)
        serialNumberColumn.type = Enums.DataTypeClass.Text
        serialNumberColumn.allowEditing = isContentEditable

        const notesColumn: ColumnModel = new ColumnModel()

        notesColumn.idName = 'notes'
        notesColumn.name = 'notes'
        notesColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Notes)
        notesColumn.type = Enums.DataTypeClass.Text

        return [orderColumn, articleColumn, serialNumberColumn, notesColumn]
    }

    //#endregion


    //#region Content

    public getIncompleteContent(orders: OpPackage[]): JQueryPromise<OpDepositoryElement[]> {
        const orderIds: number[] = _.map(orders, (order: OpPackage) => order.idPackage)

        return this.userSessionService
            .getDataProvider().Order
            .getIncompleteOrderContent(orderIds)
            .then((incompleteContent: OpDepositoryElement[]) => {
                _.forEach(incompleteContent, (contentItem: OpDepositoryElement) => {
                    const contentItemOrder: OpPackage = _.find(orders, (order: OpPackage) => order.idPackage === contentItem.idPackage)

                    if (contentItemOrder != null) {
                        contentItem.package = contentItemOrder.clone()
                    }
                })

                return OpDepositoryElement.loadNavigationProperties(this.userSessionService.getDataProvider(), incompleteContent)
            })
    }

    public getContentGridDataSourceParser(validateInfoList: OpValidateInfo[], contentItem: OpDepositoryElement) {
        const additionalFields = []
        const validateInfo: OpValidateInfo = this.getValidateInfoByContentItem(validateInfoList, contentItem)
        let errorInfo: string = null

        if (validateInfo != null) {
            errorInfo = this.getValidationMessage(validateInfo)
        }

        if (errorInfo != null) {
            additionalFields.push({
                key: 'errorInfo',
                value: errorInfo
            })
        }

        return {
            additionalFields
        }
    }

    //#endregion


    //#region Order

    public completeOrders(orders: OpPackage[]) {
        const orderIds: number[] = _.map(orders, (order: OpPackage) => order.idPackage)
        let packageResult: SequentialItemSaverResult<OpPackage>

        return this
            .getOrderDestinationDepository(orderIds)
            .then((depositoryDict: Dictionary<OpLocation>) => {
                const ordersGroupedByReceiver: Dictionary<OpPackage[]> = Dictionary.createFromGroup(orders, KeyType.Number, (order: OpPackage) => order.idOperatorReceiver)
                const packagesToCreate: OpPackage[] = this.createNewPackages(ordersGroupedByReceiver, depositoryDict)

                return this
                    .savePackages(packagesToCreate)
                    .then((packageSaveResult: SequentialItemSaverResult<OpPackage>) => {
                        const ordersToUpdate: OpPackage[] = this.createUpdatedOrders(ordersGroupedByReceiver, packageSaveResult.savedItems)

                        packageResult = packageSaveResult

                        LogHelper.log(OrderCompleteService.LOG_PREFIX, `Order complete packages save result.`, packageSaveResult)

                        if (_.size(ordersToUpdate) > 0) {
                            return this.saveOrders(ordersToUpdate)
                        }
                    })
            })
            .then((orderSaveResult: SequentialItemSaverResult<OpPackage>) => {
                this.showResultToast(packageResult)

                LogHelper.log(OrderCompleteService.LOG_PREFIX, `Order complete orders save result.`, orderSaveResult)
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator)

                LogHelper.error(OrderCompleteService.LOG_PREFIX, errorMsg)
                ToastHelper.showError(errorMsg)

                return $.Deferred().reject(error).promise()
            })
    }

    private showResultToast(packageSaveResult: SequentialItemSaverResult<OpPackage>): void {
        const savedPackagesCount = _.size(packageSaveResult.savedItems)

        if (!packageSaveResult.hasErrors) {
            ToastHelper.showSuccess(`${LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED_COUNT, PackageService.SIMAX_MODULE_NAME)} ${savedPackagesCount}`)
        } else {
            const erroredProgress = _.find(packageSaveResult.progressItems, progress => progress.status === SequentialItemSaverStatus.Error)

            if (savedPackagesCount === 0) {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(erroredProgress.error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
            } else {
                ToastHelper.showWarning(`
                    ${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CompletedWithProblems)}
                    <br />
                    ${LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED_COUNT, PackageService.SIMAX_MODULE_NAME)} ${savedPackagesCount}
                    <br />
                    ${ConvertHelper.getRequestErrorMessage(erroredProgress.error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator)}
                `)
            }
        }
    }

    private createUpdatedOrders(ordersGroupedByReceiver: Dictionary<OpPackage[]>, savedPackages: OpPackage[]): OpPackage[] {
        const ordersToUpdate: OpPackage[] = []

        _.forEach(savedPackages, (savedPackage: OpPackage) => {
            const packageOrders: OpPackage[] = ordersGroupedByReceiver.tryGetValue(savedPackage.idOperatorReceiver)

            _.forEach(packageOrders, (order: OpPackage) => {
                ordersToUpdate.push(this.createUpdatedOrder(order, savedPackage))
            })
        })

        return ordersToUpdate
    }

    private createUpdatedOrder(order: OpPackage, ordersPackage: OpPackage): OpPackage {
        const orderToUpdate: OpPackage = order.clone(false, true)

        orderToUpdate.opState = Enums.OpChangeState.Modified

        if (orderToUpdate.idPackageStatus === Enums.PackageStatus.OrderCompleted) {
            orderToUpdate.dataList.setValue(Enums.PackageStatus.OrderCompleted, Enums.DataType.HELPER_ID_PACKAGE_STATUS)
        } else if (orderToUpdate.idPackageStatus === Enums.PackageStatus.OrderPartiallyCompleted) {
            orderToUpdate.dataList.setValue(Enums.PackageStatus.OrderPartiallyCompleted, Enums.DataType.HELPER_ID_PACKAGE_STATUS)
        }

        _.forEach(orderToUpdate.contentList, (orderContentItem: OpDepositoryElement) => {
            const relatedPackageContentItem: OpDepositoryElement = _.find(ordersPackage.contentList,
                (packageContentItem: OpDepositoryElement) => orderContentItem.idDepositoryElement === packageContentItem.idReference)

            if (relatedPackageContentItem != null) {
                orderContentItem.endDate = new Date()
                orderContentItem.opState = Enums.OpChangeState.Modified
                orderContentItem.idReference = relatedPackageContentItem.idDepositoryElement
                orderContentItem.ordered = false
            }
        })

        return orderToUpdate
    }

    private createNewPackages(ordersGroupedByReceiver: Dictionary<OpPackage[]>, depositoryDict: Dictionary<OpLocation>): OpPackage[] {
        const packagesToCreate: OpPackage[] = []

        ordersGroupedByReceiver.forEach((idOperatorReceiver: number, orders: OpPackage[], index: number) => {
            const orderDepository: OpLocation = depositoryDict.tryGetValue(_.first(orders).idPackage)
            const packageToCreate: OpPackage = this.createNewPackage(idOperatorReceiver, orders, orderDepository, index)

            packagesToCreate.push(packageToCreate)
        })

        return packagesToCreate
    }

    private createNewPackage(idOperatorReceiver: number, orders: OpPackage[], orderLocation: OpLocation, index: number): OpPackage {
        const newPackage: OpPackage = new OpPackage(null)

        newPackage.contentList = []
        newPackage.creationDate = new Date()
        newPackage.idDistributor = orderLocation != null ? orderLocation.idDistributor : null
        newPackage.idOperatorCreator = this.userSessionService.getIdLoggedOperator()
        newPackage.idOperatorReceiver = idOperatorReceiver
        newPackage.idPackageStatus = Enums.PackageStatus.New
        newPackage.name = this.packageService.getNewPackageName(index)
        newPackage.received = false
        newPackage.idDistributor = orders[index].idDistributor

        _.forEach(orders, (order: OpPackage) => {
            _.forEach(order.contentList, (contentItem: OpDepositoryElement) => {
                const newContentItem: OpDepositoryElement = contentItem.clone()

                newContentItem.endDate = null
                newContentItem.idDepositoryElement = 0
                newContentItem.idPackage = order.idPackage
                newContentItem.idReference = contentItem.idDepositoryElement
                newContentItem.notes = contentItem.notes
                newContentItem.opState = Enums.OpChangeState.New
                newContentItem.ordered = true
                newContentItem.startDate = contentItem.startDate

                newPackage.contentList.push(newContentItem)
            })
        })

        return newPackage
    }

    private getOrderDestinationDepository(orderIds: number[]): JQueryPromise<Dictionary<OpLocation>> {
        return this.userSessionService
            .getDataProvider().Order
            .getOrderDestinationMagazine(orderIds)
            .then((destinationMagazines: OpPackage[]) => {
                const depositoryDict: Dictionary<OpLocation> = new Dictionary<OpLocation>(null, KeyType.Number)

                _.forEach(destinationMagazines, (destinationMagazine: OpPackage) => {
                    if (destinationMagazine != null && destinationMagazine.depositoryLocation != null) {
                        depositoryDict.addOrUpdate(destinationMagazine.idPackage, destinationMagazine.depositoryLocation)
                    }
                })

                return depositoryDict
            })
    }

    public isOrderCompletable(order: OpPackage): boolean {
        if (order != null) {
            return !_.contains([Enums.PackageStatus.OrderCompleted, Enums.PackageStatus.NewOrder, Enums.PackageStatus.OrderRemoved], order.idPackageStatus)
        }

        return false
    }

    public filterCompletableOrders(orders: OpPackage[]): OpPackage[] {
        return _.filter(orders, (order: OpPackage) => {
            return this.isOrderCompletable(order)
        })
    }

    private saveOrders(orders: OpPackage[]): JQueryPromise<SequentialItemSaverResult<OpPackage>> {
        return new SequentialItemSaver({
            itemsToSave: orders,
            mode: SequentialItemSaverMode.StopOnError,

            saveDelegate: (orderToSave: OpPackage) => {
                return this.userSessionService.getDataProvider().Order.saveOrder(orderToSave)
            },
        }).start()
    }

    private savePackages(packages: OpPackage[]): JQueryPromise<SequentialItemSaverResult<OpPackage>> {
        return new SequentialItemSaver({
            itemsToSave: packages,
            mode: SequentialItemSaverMode.StopOnError,

            saveDelegate: (packageToSave: OpPackage) => {
                return this.userSessionService.getDataProvider().Package.savePackage(packageToSave)
            },
        }).start()
    }

    //#endregion


    //#region Validation

    public getValidateInfoByContentItem(validateInfoList: OpValidateInfo[], contentItem: OpDepositoryElement): OpValidateInfo {
        return _.find(validateInfoList, (validateInfo: OpValidateInfo) => validateInfo.ReferenceValue.idDepositoryElement === contentItem.idDepositoryElement)
    }

    public getValidationMessage(validateInfo: OpValidateInfo): string {
        if (validateInfo != null) {
            const validationResult: OpValidateInfoMessage = _.first(validateInfo.Result)

            if (validationResult != null) {
                return validationResult.Message
            }
        }

        return null
    }

    public validateContent(orderContent: OpDepositoryElement[]) {
        return this.userSessionService
            .getDataProvider().Order
            .validateOrderContent(orderContent)
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator)

                LogHelper.error(OrderCompleteService.LOG_PREFIX, errorMsg)
                ToastHelper.showError(errorMsg)

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion
}

export class OrderCompleteServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [PACKAGE_SERVICE_NAME, USER_SESSION_SERVICE_NAME, function (
        packageService: PackageService,
        userSessionService: UserSessionService
    ) {
        return new OrderCompleteService(packageService, userSessionService);
    }]

    //#endregion
} 